import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout, AdminLayout } from "./components/layout/Layout";

// Public Pages
import HomePage from "./pages/HomePage";
import TripsPage from "./pages/TripsPage";
import TripDetailPage from "./pages/TripDetailPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import GalleryPage from "./pages/GalleryPage";
import ContactPage from "./pages/ContactPage";
import FishSpeciesPage from "./pages/FishSpeciesPage";
import FAQPage from "./pages/FAQPage";
import LegalPage from "./pages/LegalPage";
import CGVPage from "./pages/CGVPage";
import PrivacyPage from "./pages/PrivacyPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTripsPage from "./pages/admin/AdminTripsPage";
import AdminGalleryPage from "./pages/admin/AdminGalleryPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminInvoicesPage from "./pages/admin/AdminInvoicesPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Public Layout Wrapper
const PublicPage = ({ children }) => (
  <Layout>{children}</Layout>
);

// Admin Layout Wrapper
const AdminPage = ({ children }) => (
  <AdminLayout>{children}</AdminLayout>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicPage><HomePage /></PublicPage>} />
      <Route path="/trips" element={<PublicPage><TripsPage /></PublicPage>} />
      <Route path="/trips/:tripId" element={<PublicPage><TripDetailPage /></PublicPage>} />
      <Route path="/booking-confirmation/:bookingId" element={<PublicPage><BookingConfirmationPage /></PublicPage>} />
      <Route path="/gallery" element={<PublicPage><GalleryPage /></PublicPage>} />
      <Route path="/contact" element={<PublicPage><ContactPage /></PublicPage>} />
      <Route path="/fish-species" element={<PublicPage><FishSpeciesPage /></PublicPage>} />
      <Route path="/faq" element={<PublicPage><FAQPage /></PublicPage>} />
      <Route path="/legal" element={<PublicPage><LegalPage /></PublicPage>} />
      <Route path="/cgv" element={<PublicPage><CGVPage /></PublicPage>} />
      <Route path="/privacy" element={<PublicPage><PrivacyPage /></PublicPage>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminPage><AdminLoginPage /></AdminPage>} />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <AdminPage><AdminDashboard /></AdminPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/trips" 
        element={
          <ProtectedRoute>
            <AdminPage><AdminTripsPage /></AdminPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/gallery" 
        element={
          <ProtectedRoute>
            <AdminPage><AdminGalleryPage /></AdminPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute>
            <AdminPage><AdminAnalyticsPage /></AdminPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/invoices" 
        element={
          <ProtectedRoute>
            <AdminPage><AdminInvoicesPage /></AdminPage>
          </ProtectedRoute>
        } 
      />
      
      {/* Old Wix URL redirects for SEO */}
      <Route path="/reservation-en-ligne" element={<Navigate to="/trips" replace />} />
      <Route path="/varietes-poissons-corse-sortie-peche-en-mer" element={<Navigate to="/species" replace />} />
      <Route path="/services/*" element={<Navigate to="/trips" replace />} />
      <Route path="/commandes-en-ligne" element={<Navigate to="/trips" replace />} />
      <Route path="/online-store" element={<Navigate to="/trips" replace />} />
      <Route path="/online-store/*" element={<Navigate to="/trips" replace />} />
      <Route path="/book-online" element={<Navigate to="/trips" replace />} />
      <Route path="/les-peches" element={<Navigate to="/trips" replace />} />
      <Route path="/about" element={<Navigate to="/" replace />} />
      <Route path="/blank" element={<Navigate to="/" replace />} />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
